<template>
  <EditableList
    :items="modelValue"
    add-label="Add section"
    remove-label="Remove section"
    @add="addSection"
    @remove="removeSection"
  >
    <template #row="{ item: section, index }">
      <div class="section-fields">
        <StField label="Name">
          <StTextInput
            :model-value="section.name"
            placeholder="Section name"
            @update:model-value="updateField(index, 'name', $event)"
          />
        </StField>

        <StField label="Reference">
          <StSelect
            :model-value="section.ref ?? 'none'"
            @update:model-value="updateRef(index, String($event))"
          >
            <option value="none">none</option>
            <option value="battleplans">battleplans</option>
          </StSelect>
        </StField>

        <template v-if="!section.ref">
          <div class="field--full">
            <RuleContainerForm
              :model-value="section.containers"
              @update:model-value="updateField(index, 'containers', $event)"
            />
          </div>

          <div class="field--full">
            <RuleSectionForm
              :model-value="section.subSections"
              @update:model-value="updateField(index, 'subSections', $event)"
            />
          </div>
        </template>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import type { RuleSection, BundleRef } from '@scrolldex/shared';
import { ModeOfPlay } from '@scrolldex/shared';
import { StField, StTextInput, StSelect } from '../ui/index.js';
import EditableList from '../EditableList.vue';
import RuleContainerForm from './RuleContainerForm.vue';

// Vue SFCs self-register by filename for recursion — no explicit import needed
// for RuleSectionForm itself.

const props = defineProps<{
  modelValue: RuleSection[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: RuleSection[]): void;
}>();

function newSection(): RuleSection {
  return {
    name: '',
    modeOfPlay: ModeOfPlay.Any,
    subSections: [],
    containers: [],
  };
}

function addSection() {
  emit('update:modelValue', [...props.modelValue, newSection()]);
}

function removeSection(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

function updateField(index: number, field: keyof RuleSection, value: unknown) {
  const updated = props.modelValue.map((section, i) => {
    if (i !== index) return section;
    return { ...section, [field]: value };
  });
  emit('update:modelValue', updated);
}

function updateRef(index: number, refValue: string) {
  const updated = props.modelValue.map((section, i) => {
    if (i !== index) return section;
    if (refValue === 'none') {
      const { ref: _omit, ...rest } = section;
      return { ...rest };
    }
    return { ...section, ref: refValue as BundleRef };
  });
  emit('update:modelValue', updated);
}
</script>

<style scoped>
.section-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--st-space-2);
  align-items: end;
  width: 100%;
}

.section-fields :deep(.field--full) {
  grid-column: 1 / -1;
}
</style>
